import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { PropType } from 'vue';
import { notification, Tabs } from 'ant-design-vue';
import MultipositionHeader from '@/components/global/popup/MultipositionHeader.vue';
import ProductCombination from '@/components/views/productView/ProductCombination.vue';
import CombinationsSelectorPopup from '@/components/global/popup/CombinationsSelectorPopup.vue';
import CombinationsEditPopup from '@/components/global/popup/CombinationsEditPopup.vue';
import ProductSlotRow from '@/components/global/popup/ProductSlotRow.vue';
import { MultipositionDeleteMap } from '@/interfaces/general/MultipositionDeleteMap';
import { ProductRowItem } from '@/interfaces/general/ProductRowItem';
import { ProductCombinations } from '@/interfaces/components/projectNew/ProductCombinations';
import { RadioButtonGroupOptions } from '@/interfaces/components/RadioButtonGroupOptions';
import { debounce } from 'vue-debounce';
import ConfiguratorService from '@/services/ConfiguratorService';
import { UpdateProductFormEntry } from '@/interfaces/components/configurator/UpdateProductFormEntry';
import {
    createNewRowsWithPastedData,
    determineIfSingleValueIsPasted,
    extractCellsAsArraysFromString,
    updateExistingRowsWithPastedData,
} from '@/helpers/FormDataHelper';
import { ChecklistFieldEntry } from '@/interfaces/components/configurator/ChecklistFieldEntry';
import OfferRepository from '@/repositories/OfferRepository';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { setRowColor } from '@/helpers/FormDataHelper';
import { CustomColors } from '@/enums/global/CustomColors';
import { combinationsConfiguration } from '@/modules/ConfiguratorCombinations/helpers/CombinationsConfiguration';
import OfferItem from '@/models/OfferItem';
import { jointsConfiguration } from '@/modules/ConfiguratorJoints/helpers/JointsConfiguration';
import i18n from '@/i18n';
import store from '@/store';
import { resetCombinationSwitchField } from '@/modules/ConfiguratorCombinations/helpers/CombinationsHelper';
import {
    getNextCombinationConnection,
    syncCombinationConnectionCounter,
} from '@/modules/ConfiguratorCombinations/helpers/CombinationsConnectionAllocator';


export default /*#__PURE__*/_defineComponent({
  __name: 'SegmentedMultiposition',
  props: {
    deleteExistingRow: {
        type: Function as PropType<(multipositionDeleteMap: MultipositionDeleteMap) => void>,
        required: true,
    },
    addNewRow: {
        type: Function as PropType<
            (numberOfRows: number, existingProductValues?: ChecklistFieldEntry[]) => void | Promise<void>
        >,
        default: null,
    },
    addNewRowInEditMode: {
        type: Function as PropType<
            (firstActiveItemRow: ProductRowItem, existingProductValues: ChecklistFieldEntry[]) => void | Promise<void>
        >,
        default: null,
    },
    updateItemRowsIsUpdatingState: {
        type: Function as PropType<(itemRowIndex: number, newState: boolean) => void>,
        default: null,
    },
    isUpdatingData: { type: Boolean, required: true },
    isAddingProducts: { type: Boolean, default: false },
    isCustomProduct: { type: Boolean, default: false },
    activeItemRows: { type: Array as PropType<ProductRowItem[]>, required: true },
    numberOfProductForms: { type: Number, default: 1 },
    offerId: { type: String, required: true },
    availableSegments: { type: Array as PropType<RadioButtonGroupOptions[]>, required: true },
    isWindowCombinationWizard: { type: Boolean, required: false, default: false },
    productDetails: { type: Object as any, required: false, default: null },
    currentUserGroup: {
        type: [String, Object] as PropType<string | { id: string; name: string } | null>,
        required: false,
        default: null,
    },
},
  emits: ['isLockedSegment'],
  setup(__props, { expose, emit }) {

const props = __props;

const TabPane = Tabs.TabPane;

/* === Props & Emits === */



const { proxy } = getCurrentInstance()!;
const componentProxy = proxy as any;

/* === State & Store Access === */
const selectedSegment = ref<string>();
const hiddenIndices = ref<boolean[]>([]);
const level = ref(2);
const selectedCombinationRows = ref<ProductRowItem[]>([]);
const combinationsSelectorActive = ref(false);
const combinationsConfiguratorSelectorActive = ref(false);
const count = ref(0);
const penultimateSelectedLevel = ref(0);
const combinationImages = ref<Record<string | number, { image: string; type: 'sestav' | 'kotni' }>>({});
const removedConnectionsByGroupKey = ref<Record<string, number>>({});
const removedConnectionIds = ref<Set<number>>(new Set());

const updateActiveProductFormValue = (entry: UpdateProductFormEntry) =>
    store.dispatch('configurator/updateActiveProductFormValue', entry);

/* === Segment & Configurator State === */
const showCombinations = computed(() => {
    const currentSegment = props.availableSegments.find((seg) => seg.value === selectedSegment.value);
    return currentSegment?.isLocked;
});

const currentCombinationsConfiguration = computed(() => {
    if (!selectedSegment.value) {
        return null;
    }

    const itemRow = props.activeItemRows[0];

    // Find configuration that matches the current segment
    const segmentConfig = combinationsConfiguration.find(
        (config) =>
            config.products.includes(props.productDetails.productSystemName) ||
            config.products.includes(itemRow?.offerItem?.name || ''),
    );

    return segmentConfig || null;
});

const currentJointsConfiguration = computed(() => {
    if (!selectedSegment.value) {
        return null;
    }

    const itemRow = props.activeItemRows[0];

    // Find configuration that matches the current segment
    const segmentConfig = jointsConfiguration.find(
        (config) =>
            config.products.includes(props.productDetails.productSystemName) ||
            config.products.includes(itemRow?.offerItem?.name || ''),
    );

    return segmentConfig || null;
});

const isUsingCombinationsConfigurator = computed(() => {
    if (!selectedSegment.value) {
        return false;
    }

    const currentConfig = currentCombinationsConfiguration.value;

    if (!currentConfig) {
        return false;
    }

    // We show for every tab now
    return true;

    // Normalize both segment names before comparison to handle different languages
    // return normalizeSegmentName(currentConfig.segment) === normalizeSegmentName(selectedSegment.value);
});

const isUsingStandardCombinationsConfigurator = computed(() => {
    if (!isUsingCombinationsConfigurator.value) {
        return false;
    }

    const currentConfig = currentCombinationsConfiguration.value;

    return currentConfig?.configuratorType === 'standard';
});

const isUsingAlternativeCombinationsConfigurator = computed(() => {
    if (!isUsingCombinationsConfigurator.value) {
        return false;
    }

    const currentConfig = currentCombinationsConfiguration.value;

    return currentConfig?.configuratorType !== 'standard';
});

const isUsingJointsConfigurator = computed(() => {
    const currentConfig = currentJointsConfiguration.value;

    return !!currentConfig?.configuratorType;
});

const productCombinations = computed(() => {
    const combinations: ProductCombinations = {};

    for (const row of props.activeItemRows) {
        if (row.connection === null) {
            continue;
        }

        if (!combinations.hasOwnProperty(row.connection)) {
            componentProxy.$set(combinations, row.connection, []);
        }

        combinations[row.connection].push(row);
    }

    return combinations;
});

const rowsWithNoConnection = computed(() =>
    props.activeItemRows.filter((row) => {
        return row.connection === null;
    }),
);

const manualCombinationsButtonText = computed(() => {
    if (!isUsingStandardCombinationsConfigurator.value) {
        return i18n.t('Add combination') as string;
    }

    return i18n.t('Add sestav manually') as string;
});

/* === Combination Management === */
function getRowGroupKey(rows: ProductRowItem[]): string {
    return rows
        .map((row) => (row.offerItem ? `oi:${row.offerItem.id}` : `pf:${row.activeProductFormId}`))
        .sort()
        .join('|');
}

function onRemoveCombinationConnection(rows: ProductRowItem[]) {
    const connectionId = rows[0]?.connection ?? null;
    if (connectionId !== null) {
        const groupKey = getRowGroupKey(rows);
        removedConnectionsByGroupKey.value[groupKey] = connectionId;
        removedConnectionIds.value.add(connectionId);
    }
    rows.forEach((row: ProductRowItem) => {
        row.connection = null;
        row.color = '';
        // Reset combination form field to false
        resetCombinationSwitchField(row, currentCombinationsConfiguration.value, updateActiveProductFormValue);
    });

    if (connectionId !== null) {
        // Remove combination image if it exists
        componentProxy.$delete(combinationImages.value, String(connectionId));
        removedConnectionIds.value.add(connectionId);
    }

    notification.success({
        message: i18n.t('Connection removed!') as string,
        description: '',
    });
}

function onEditCombination(rows: ProductRowItem[]) {
    selectedCombinationRows.value = rows;
    EventBus.$emit(EventBusEvents.openCombinationsEditPopup);
}

function openCombinationsSelector() {
    EventBus.$emit(EventBusEvents.openCombinationsSelectorPopup);
    combinationsSelectorActive.value = true;
}

function openCombinationsConfiguratorSelector() {
    EventBus.$emit(EventBusEvents.openCombinationsConfiguratorSelectorPopup, {
        rows: props.activeItemRows,
        productName: props.productDetails.productSystemName,
    });
    combinationsConfiguratorSelectorActive.value = true;
}

function openJointsConfiguratorSelector() {
    EventBus.$emit(EventBusEvents.openJointsConfiguratorSelectorPopup, {
        rows: props.activeItemRows,
        productName: props.productDetails.productSystemName,
    });
}

function resetCombinationsSelectorStatus() {
    combinationsSelectorActive.value = false;
}

const productCombinationPayload = computed(() => ({
    hiddenIndices: hiddenIndices.value,
    isLoading: props.isUpdatingData,
    offer: offer.value,
    isCustomProduct: props.isCustomProduct,
    level: level.value,
    d: props.deleteExistingRow,
    isEditMode: false,
    isUpdating: true,
    selectedSegment: selectedSegment.value,
    updateItemRowsIsUpdatingState: props.updateItemRowsIsUpdatingState,
}));

function addNewCombination(productFormIds: number[], connectionId?: number) {
    const colors: Record<string | number, any> = { ...CustomColors };

    const selectedRows = props.activeItemRows.filter((rowItem: ProductRowItem) =>
        productFormIds.includes(rowItem.activeProductFormId),
    );

    if (connectionId === undefined) {
        const groupKey = getRowGroupKey(selectedRows);
        if (removedConnectionsByGroupKey.value[groupKey] !== undefined) {
            connectionId = removedConnectionsByGroupKey.value[groupKey];
            delete removedConnectionsByGroupKey.value[groupKey];
            removedConnectionIds.value.delete(connectionId);
        } else {
            connectionId = getNextCombinationConnection();
        }
    }

    selectedRows.forEach((selectedRowItem) => {
        selectedRowItem.connection = connectionId!;
    });

    if (props.isAddingProducts) {
        props.activeItemRows.forEach((productRowItem) => {
            setRowColor(productRowItem, colors);
        });
    }
}

function onConfiguratorCombinationEvent(
    rows: ProductRowItem[],
    addConnection?: boolean,
    combinationImageBase64?: string | null,
    productSystemName?: string,
    type: 'sestav' | 'kotni' = 'sestav',
) {
    if (productSystemName && productSystemName !== props.productDetails?.productSystemName) {
        return;
    }
    updateCombinationFromConfigurator(rows, addConnection, combinationImageBase64, type);
}

const debouncedUpdateHiddenIndicesInMultiposition = debounce(() => {
    updateHiddenIndicesInMultiposition();
}, 200);

const debouncedUpdateHiddenIndices = debounce(() => {
    updateHiddenIndices();
}, 200);

const allSegments = computed(() => [
    {
        label: i18n.t('Svi'),
        value: 'All',
    },
    ...props.availableSegments,
]);

const offer = computed(() => {
    if (props.offerId == null) {
        return null;
    }

    return OfferRepository.getOfferById(props.offerId);
});

function switchLevel(newLevel: number) {
    level.value = -1;
    nextTick(() => {
        level.value = newLevel;

        if (!showCombinations.value || !props.isWindowCombinationWizard) {
            penultimateSelectedLevel.value = newLevel;
        }
    });
}

function handleConnection(e: number) {
    props.activeItemRows.forEach((row) => {
        row.connection = e;
    });
}

function handleProduction(e: boolean) {
    props.activeItemRows.forEach((row) => {
        row.production = e;
    });
}

/* === Visibility & Row Widths === */
async function updateHiddenIndices() {
    if (
        selectedSegment.value == null ||
        (!Object.keys(productCombinations.value).length && showCombinations.value && !combinationsSelectorActive.value)
    ) {
        return;
    }

    let productRowRefs: any[];

    // In case product combinations are used, we dig for refs inside child component
    if (showCombinations.value && !combinationsSelectorActive.value) {
        await nextTick();
        productRowRefs = (componentProxy.$refs?.productCombination as any[])
            ?.map((combinationRef: any) => {
                return combinationRef.$refs.productSlotRow as any[];
            })
            .flat();
    } else if (showCombinations.value && combinationsSelectorActive.value) {
        await nextTick();
        productRowRefs = (
            ((componentProxy.$refs?.combinationsSelector as any)?.$refs.productCombination as any).$refs
                .productSlotRow as any[]
        ).flat();
    } else {
        productRowRefs = componentProxy.$refs.productSlotRow as any[];
    }

    const allDisabledStates = ConfiguratorService.getAllDisabledStatesFromRow(productRowRefs);

    const firstRow = ConfiguratorService.getInitialColumnVisibilityBasedOnSelectedSegment(
        props.activeItemRows[0],
        selectedSegment.value,
    );

    hiddenIndices.value = ConfiguratorService.determineColumnVisibility(firstRow, allDisabledStates);
}

function updateHiddenIndicesInMultiposition() {
    updateHiddenIndices();
    updateWidthOfProductSlotWrappers();
}

function updateWidthOfProductSlotWrappers() {
    // in case of combinations, its updated in a separate component
    if (showCombinations.value) {
        EventBus.$emit(EventBusEvents.updateWidthOfProductSlotWrappers);
        return;
    }
    // needs to be updated if there are significant width changes in the DOM
    // otherwise, the sticky columns won't work properly
    nextTick(async () => {
        let wrapperElements;

        wrapperElements = [
            ...(componentProxy.$refs.productSlotRow as any[]).map(
                (productSlotRow: any) => productSlotRow.$refs.wrapper,
            ),
            (componentProxy.$refs.multipositionHeader as any).$refs.wrapper,
        ] as HTMLElement[];

        for await (const wrapperElement of wrapperElements) {
            wrapperElement.style.width = 'auto';
            await nextTick();
            wrapperElement.style.width = `${wrapperElement.scrollWidth}px`;
        }
    });
}

function radioButtonChange(segment: string) {
    selectedSegment.value = segment;
}

/* === Clipboard Paste === */
function onPaste(evt: any) {
    if (evt.clipboardData == null || props.isUpdatingData || props.isCustomProduct) {
        return;
    }

    let cells = extractCellsAsArraysFromString(evt.clipboardData.getData('text'));

    if (determineIfSingleValueIsPasted(cells)) {
        evt.preventDefault();
    } else {
        return;
    }

    const productSlotRowElement = evt.target.closest('.c-add-product-row__wrapper');
    const productSlotRowNumber = productSlotRowElement ? productSlotRowElement.dataset.rowNumber : null;
    if (cells.length > 50) {
        cells = cells.slice(0, 50);
        notification.warn({
            message: (i18n.t('Započeto kopiranje __NUMBEROFITEMS__ pozicija') as string).replace(
                '__NUMBEROFITEMS__',
                String(cells.length),
            ),
            // tslint:disable-next-line:max-line-length
            description: i18n.t(
                'Limit zaljepljenih proizvoda je 50 stoga će se zaljepiti samo prvih 50 proizvoda',
            ) as string,
        });
    } else {
        notification.info({
            message: (i18n.t('Započeto kopiranje __NUMBEROFITEMS__ pozicija') as string).replace(
                '__NUMBEROFITEMS__',
                String(cells.length),
            ),
            description: i18n.t('Ova akcija može potrajati neko vrijeme stoga molimo za strpljenje') as string,
        });
    }

    const widthElement = productSlotRowElement
        .querySelector('[data-test-id="p7402"]')
        ?.closest('.is-multiposition') as HTMLElement;

    const heightElement = productSlotRowElement
        .querySelector('[data-test-id="p7403"]')
        ?.closest('.is-multiposition') as HTMLElement;

    const isAlternativeFormForPasting =
        !widthElement.classList.contains('is-multiposition--visible') &&
        !heightElement.classList.contains('is-multiposition--visible');

    updateExistingRowsWithPastedData(props.activeItemRows, cells, productSlotRowNumber, isAlternativeFormForPasting);
    createNewRowsWithPastedData(
        cells,
        props.activeItemRows,
        props.addNewRow!,
        props.addNewRowInEditMode!,
        productSlotRowNumber,
        isAlternativeFormForPasting,
    );
}

/* === Configurator Level === */
function setConfiguratorLevel() {
    let isExpertUG = false;

    // Allow expert level for specific user groups using id or name and match by id or name for edge cases
    const expertUserGroups = ['1', '2', '3'];
    const expertUGNames = ['ALUBOMA'];

    if (typeof props.currentUserGroup === 'string') {
        isExpertUG = expertUserGroups.includes(props.currentUserGroup);
    } else if (props.currentUserGroup && typeof props.currentUserGroup === 'object') {
        isExpertUG =
            expertUserGroups.includes(String(props.currentUserGroup.id)) ||
            expertUGNames.includes(String(props.currentUserGroup.name));
    }

    if (isExpertUG) {
        level.value = 2;
    } else {
        level.value = 0;
    }

    penultimateSelectedLevel.value = level.value;
}

function getCombinationImages(): Record<string | number, { image: string; type: 'sestav' | 'kotni' }> {
    return combinationImages.value;
}

function getRemovedConnectionIds(): number[] {
    return Array.from(removedConnectionIds.value);
}
function clearRemovedConnectionIds(): void {
    removedConnectionIds.value.clear();
}

async function updateCombinationFromConfigurator(
    selectedRows: ProductRowItem[],
    addConnection: boolean = true,
    combinationImageBase64: string | null = null,
    type: 'sestav' | 'kotni' = 'sestav',
) {
    const selectedIds = new Set(selectedRows.map((r) => r.activeProductFormId));
    const matchingActiveRows = props.activeItemRows.filter((r) => selectedIds.has(r.activeProductFormId));

    if (matchingActiveRows.length === 0) {
        return;
    }

    const existingConnections = matchingActiveRows
        .map((r) => r.connection)
        .filter((c): c is number => c !== null && c !== undefined);

    const allSameConnection =
        existingConnections.length === matchingActiveRows.length && new Set(existingConnections).size === 1;

    let connectionId: number;
    const groupKey = getRowGroupKey(matchingActiveRows);

    if (allSameConnection) {
        connectionId = existingConnections[0];
    } else if (removedConnectionsByGroupKey.value[groupKey] !== undefined) {
        connectionId = removedConnectionsByGroupKey.value[groupKey];
        delete removedConnectionsByGroupKey.value[groupKey];
        removedConnectionIds.value.delete(connectionId);
    } else {
        connectionId = getNextCombinationConnection();
    }

    props.activeItemRows.forEach((row: ProductRowItem) => {
        if (selectedIds.has(row.activeProductFormId)) {
            row.connection = connectionId;
        }
    });

    if (props.isAddingProducts) {
        const colors: Record<string | number, any> = { ...CustomColors };
        props.activeItemRows.forEach((productRowItem) => {
            setRowColor(productRowItem, colors);
        });
    }

    if (combinationImageBase64 && selectedRows.length > 0) {
        componentProxy.$set(combinationImages.value, String(connectionId), { image: combinationImageBase64, type });
    }
}

async function initCombinationCounter() {
    const existingItems = OfferItem.query().where('offer_id', props.offerId).get();

    const existingConnections = existingItems
        .map((item: any) => item.connection)
        .filter((c: any): c is number => c !== null && c !== undefined && typeof c === 'number');

    if (existingConnections.length > 0) {
        syncCombinationConnectionCounter(Math.max(...existingConnections));
    }
}

/* === Watchers === */
function onAvailableSegmentsChange() {
    selectedSegment.value = props.availableSegments[0]?.value as string;
}

async function onSelectedSegmentChange() {
    debouncedUpdateHiddenIndicesInMultiposition();
    emit('isLockedSegment', showCombinations.value);
    if (showCombinations.value && props.isWindowCombinationWizard) {
        switchLevel(2);
    } else {
        switchLevel(penultimateSelectedLevel.value);
    }

    if (showCombinations.value && count.value === 0) {
        count.value++;
    }
}

function onDeleteRow(row: ProductRowItem) {
    console.log('Deleting row:', row);
    const hasConnection = row.connection !== null;

    if (hasConnection) {
        const connectionId = row.connection as number;
        const connectedRows = props.activeItemRows.filter(
            (itemRow) => itemRow !== row && itemRow.connection === connectionId,
        );

        if (connectedRows.length) {
            console.log('Resetting connection for connected rows:', connectedRows, connectionId);
            connectedRows.forEach((connectedRow) => {
                connectedRow.connection = null;
                connectedRow.color = '';
                resetCombinationSwitchField(
                    connectedRow,
                    currentCombinationsConfiguration.value,
                    updateActiveProductFormValue,
                );
            });
        }

        componentProxy.$delete(combinationImages.value, String(connectionId));
        console.log(combinationImages.value);
        removedConnectionIds.value.add(connectionId);
    }

    props.deleteExistingRow({
        activeProductFormId: row.activeProductFormId,
        offerItemId: row.offerItem ? row.offerItem.offerItemId : null,
    });
}

watch(() => props.availableSegments, onAvailableSegmentsChange, { deep: true, immediate: true });
watch(selectedSegment, onSelectedSegmentChange, { deep: true });

/* === Lifecycle === */
onMounted(async () => {
    componentProxy.$el.addEventListener('paste', onPaste);
    EventBus.$on(EventBusEvents.addNewCombinationFromConfigurator, onConfiguratorCombinationEvent);
    setConfiguratorLevel();
    await initCombinationCounter();
});

onBeforeUnmount(() => {
    componentProxy.$el.removeEventListener('paste', onPaste);
    EventBus.$off(EventBusEvents.addNewCombinationFromConfigurator, onConfiguratorCombinationEvent);
});

expose({
    getCombinationImages,
    getRemovedConnectionIds,
    clearRemovedConnectionIds,
    openCombinationsConfiguratorSelector,
    openJointsConfiguratorSelector,
    switchLevel,
});

return { __sfc: true,TabPane, props, emit, proxy, componentProxy, selectedSegment, hiddenIndices, level, selectedCombinationRows, combinationsSelectorActive, combinationsConfiguratorSelectorActive, count, penultimateSelectedLevel, combinationImages, removedConnectionsByGroupKey, removedConnectionIds, updateActiveProductFormValue, showCombinations, currentCombinationsConfiguration, currentJointsConfiguration, isUsingCombinationsConfigurator, isUsingStandardCombinationsConfigurator, isUsingAlternativeCombinationsConfigurator, isUsingJointsConfigurator, productCombinations, rowsWithNoConnection, manualCombinationsButtonText, getRowGroupKey, onRemoveCombinationConnection, onEditCombination, openCombinationsSelector, openCombinationsConfiguratorSelector, openJointsConfiguratorSelector, resetCombinationsSelectorStatus, productCombinationPayload, addNewCombination, onConfiguratorCombinationEvent, debouncedUpdateHiddenIndicesInMultiposition, debouncedUpdateHiddenIndices, allSegments, offer, switchLevel, handleConnection, handleProduction, updateHiddenIndices, updateHiddenIndicesInMultiposition, updateWidthOfProductSlotWrappers, radioButtonChange, onPaste, setConfiguratorLevel, getCombinationImages, getRemovedConnectionIds, clearRemovedConnectionIds, updateCombinationFromConfigurator, initCombinationCounter, onAvailableSegmentsChange, onSelectedSegmentChange, onDeleteRow, Tabs, MultipositionHeader, ProductCombination, CombinationsSelectorPopup, CombinationsEditPopup, ProductSlotRow }
}

})